import { Autocomplete, Checkbox, Collapse, FormControlLabel, FormGroup, TextField } from "@mui/material";
import React from "react";
import { useIntl } from "react-intl";

import { type InputProps, OTHER, answerIds } from "../answers";
import { useReadOnly } from "../readOnly";

import { chipSlot, fieldSx, optionSx } from "./optionSx";
import { OtherField } from "./OtherField";
import { SEARCHABLE_FROM } from "./singleSelect";

export const MultiSelectInput: React.FC<InputProps<"multiSelect">> = ({
    question,
    answer,
    onChange,
    error,
    compact,
}) => {
    const readOnly = useReadOnly();
    const { formatMessage } = useIntl();
    const selected = answerIds(answer?.value);
    const toggle = (id: string, checked: boolean) =>
        onChange({ ...answer, value: checked ? [...selected, id] : selected.filter((s) => s !== id) });
    const choices = [
        ...question.options,
        ...(question.allowOther ? [{ id: OTHER, label: formatMessage({ defaultMessage: "Other" }) }] : []),
    ];

    if (compact || question.options.length > SEARCHABLE_FROM) {
        return (
            <>
                <Autocomplete
                    disabled={readOnly}
                    size="small"
                    autoHighlight
                    multiple
                    disableCloseOnSelect
                    options={choices.map((c) => c.id)}
                    getOptionLabel={(id) => choices.find((c) => c.id === id)?.label ?? id}
                    value={selected}
                    onChange={(_, value) => onChange({ ...answer, value })}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            error={error}
                            placeholder={formatMessage({ defaultMessage: "Search or select" })}
                        />
                    )}
                    slotProps={{ chip: chipSlot }}
                    sx={fieldSx}
                />
                <Collapse in={selected.includes(OTHER)} unmountOnExit>
                    <OtherField answer={answer} onChange={onChange} error={error} />
                </Collapse>
            </>
        );
    }

    return (
        <FormGroup sx={{ gap: 0.5 }}>
            {choices.map((choice) => (
                <FormControlLabel
                    sx={optionSx}
                    key={choice.id}
                    label={choice.label}
                    control={
                        <Checkbox
                            checked={selected.includes(choice.id)}
                            onChange={(event) => toggle(choice.id, event.target.checked)}
                        />
                    }
                />
            ))}
            <Collapse in={selected.includes(OTHER)} unmountOnExit>
                <OtherField answer={answer} onChange={onChange} error={error} />
            </Collapse>
        </FormGroup>
    );
};
